import React, { useState } from 'react';
import { useSelector, shallowEqual } from 'react-redux';
import { Alert, AlertIcon, Box, Button } from '@chakra-ui/react';
import { parseDate, formatDate, CardContainer, CostItem } from 'shared-components';

import DepositModal from './modal';
import { ApplicationState } from '../../../../store';
import { FundingSummary } from '../../../../types';
import { formatCurrency } from '../../../../utilities/formatting';

interface IProps {
  forceShow: boolean;
  fundingSummary: FundingSummary;
  onSave: () => void;
}

function DepositFunding(props: IProps) {
  const { forceShow, fundingSummary, onSave } = props;

  const student = useSelector(({ student }: ApplicationState) => student, shallowEqual);

  const {
    courseFeeDepositReqd: prevCourseFeeDepositReqd,
    courseFeeDeposit: prevCourseFeeDeposit = 0,
    courseFeeDepositDate: prevCourseFeeDepositDate,
    accomDepositReqd: prevAccomDepositReqd,
    accomDeposit: prevAccomDeposit = 0,
    accomDepositDate: prevAccomDepositDate,
  } = student.financials;

  const courseFeeDepositActive = prevCourseFeeDepositReqd === 'Yes';
  const courseFeeDepositExpected = prevCourseFeeDepositReqd === 'No';

  const accomDepositActive = prevAccomDepositReqd === 'Yes';
  const accomDepositExpected = prevAccomDepositReqd === 'Not yet - I plan on paying one';

  const [showModal, setShowModal] = useState(forceShow);

  const onOpenModal = () => {
    setShowModal(true);
  };

  const onCloseModal = () => {
    setShowModal(false);
  };

  const renderItems = () => {
    const nodes: React.ReactNode[] = [];

    if (!courseFeeDepositActive && !courseFeeDepositExpected && !accomDepositActive && !accomDepositExpected) {
      nodes.push(
        <Box key="deposit-none" data-selector="deposit-none">
          You have specified that you do not have to pay a deposit.
        </Box>,
      );
    }

    if (courseFeeDepositActive) {
      nodes.push(
        <CostItem
          key="course-fee-deposit-item"
          id="course-fee-deposit-item"
          label="Course Fee Deposit"
          value={`£${formatCurrency(prevCourseFeeDeposit)}`}
          hasEdit={!student.config.depositUneditable}
          hasClose={false}
          onEditClick={onOpenModal}
        />,
      );
    }
    if (accomDepositActive) {
      nodes.push(
        <CostItem
          key="accom-deposit-item"
          id="accom-deposit-item"
          label="Accommodation Deposit"
          value={`£${formatCurrency(prevAccomDeposit)}`}
          hasEdit={!student.config.depositUneditable}
          hasClose={false}
          onEditClick={onOpenModal}
        />,
      );
    }

    const pluralDeposits = courseFeeDepositExpected && accomDepositExpected;

    if (courseFeeDepositExpected || accomDepositExpected) {
      nodes.push(
        <React.Fragment key="deposit-expected">
          <Alert mb={3} data-selector="deposit-expected">
            <AlertIcon />
            Update your deposit status{pluralDeposits && 'es'} here once {pluralDeposits ? 'they have' : 'it has'} been
            paid.
          </Alert>

          {courseFeeDepositExpected && (
            <CostItem
              label="Course fee deposit"
              value={<>Expected {formatDate(parseDate(prevCourseFeeDepositDate), 'DD MMM YYYY')}</>}
              data-selector="deposit-expected-course"
            />
          )}

          {accomDepositExpected && (
            <CostItem
              label="Accommodation deposit"
              value={<>Expected {formatDate(parseDate(prevAccomDepositDate), 'DD MMM YYYY')}</>}
              data-selector="deposit-expected-accom"
            />
          )}
        </React.Fragment>,
      );
    }

    return nodes;
  };

  const renderButton = () => {
    const amount = fundingSummary.totalRemaining;
    const buttonVariant = amount <= 0 || courseFeeDepositActive || accomDepositActive ? 'secondary' : 'solid';

    return (
      <>
        {!student.config.depositUneditable && (
          <Button
            id="add-deposit"
            data-selector="add-deposit"
            onClick={onOpenModal}
            variant={buttonVariant}
            size={'lg'}
          >
            {courseFeeDepositActive || accomDepositActive ? 'I have paid another deposit' : 'Add a deposit'}
          </Button>
        )}
      </>
    );
  };

  const renderModal = () => {
    return <DepositModal forceShow={forceShow} onSave={onSave} onClose={onCloseModal} />;
  };

  if (
    !courseFeeDepositActive &&
    !courseFeeDepositExpected &&
    !accomDepositActive &&
    !accomDepositExpected &&
    student.config.depositUneditable === true
  ) {
    return <></>;
  }
  return (
    <CardContainer title="Deposits Paid">
      <>
        {renderItems()}
        {renderButton()}
        {showModal ? renderModal() : null}
      </>
    </CardContainer>
  );
}

// Removed unused styled components InfoText and InfoDate

export default DepositFunding;
